import React from 'react';
import { Mic, MicOff, AudioWaveform as Waveform } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface AudioChatProps {
  postId: string;
}

class CallsApp {
  private prefixPath: string;
  private sessionId?: string;

  constructor(appId: string, basePath = 'https://rtc.live.cloudflare.com/v1') {
    this.prefixPath = `${basePath}/apps/${appId}`;
  }
  
  async getTurnCredentials() {
    const response = await fetch('https://rtc.live.cloudflare.com/v1/turn/keys/633728da0b47a5c7d4617be2c90bfa9c/credentials/generate', {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer 434bf6db8df409c60e368f3da223eb655e9d44b537e3d9bdd7083beb3b0c4d9f',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ttl: 86400 })
    });
    return response.json();
  }

  async sendRequest(url: string, body: any, method = 'POST') {
    const request = {
      method,
      mode: 'cors' as RequestMode,
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer 578310b4fac7ac7d7ee245fcf24dcf4bca730c088fc9c7869c9ce81a32f9c1bd`
      },
      body: JSON.stringify(body)
    };
    const response = await fetch(url, request);
    const result = await response.json();
    return result;
  }

  checkErrors(result: any, tracksCount = 0) {
    if (result.errorCode) {
      throw new Error(result.errorDescription);
    }
    for (let i = 0; i < tracksCount; i++) {
      if (result.tracks[i].errorCode) {
        throw new Error(`tracks[${i}]: ${result.tracks[i].errorDescription}`);
      }
    }
  }

  async newSession(offerSDP: string) {
    const url = `${this.prefixPath}/sessions/new`;
    const body = {
      sessionDescription: {
        type: 'offer',
        sdp: offerSDP
      }
    };
    const result = await this.sendRequest(url, body);
    this.checkErrors(result);
    this.sessionId = result.sessionId;
    return result;
  }

  async newTracks(trackObjects: any[], offerSDP: string | null = null) {
    if (!this.sessionId) throw new Error('No active session');
    const url = `${this.prefixPath}/sessions/${this.sessionId}/tracks/new`;
    const body = {
      sessionDescription: offerSDP ? {
        type: 'offer',
        sdp: offerSDP
      } : undefined,
      tracks: trackObjects
    };
    const result = await this.sendRequest(url, body);
    this.checkErrors(result, trackObjects.length);
    return result;
  }

  async sendAnswerSDP(answer: string) {
    if (!this.sessionId) throw new Error('No active session');
    const url = `${this.prefixPath}/sessions/${this.sessionId}/renegotiate`;
    const body = {
      sessionDescription: {
        type: 'answer',
        sdp: answer
      }
    };
    const result = await this.sendRequest(url, body, 'PUT');
    this.checkErrors(result);
  }
}

export function AudioChat({ postId }: AudioChatProps) {
  const [isAudioEnabled, setIsAudioEnabled] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [participants, setParticipants] = React.useState<Set<string>>(new Set());
  const pcRef = React.useRef<RTCPeerConnection | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const appRef = React.useRef<CallsApp | null>(null);
  const participantsRef = React.useRef<Set<string>>(new Set());

  // Handle presence subscription and tracking
  React.useEffect(() => {
    let userId = crypto.randomUUID();
    
    const channel = supabase
      .channel(`audio:${postId}`)
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const currentParticipants = new Set(Object.keys(state));
        participantsRef.current = currentParticipants;
        setParticipants(currentParticipants);
      })
      .subscribe();

    // Track user presence when audio is enabled
    const trackPresence = async () => {
      if (isAudioEnabled) {
        try {
          await channel.track({ user: userId });
        } catch (err) {
          console.error('Failed to track presence:', err);
        }
      } else {
        try {
          await channel.untrack();
        } catch (err) {
          console.error('Failed to untrack presence:', err);
        }
      }
    };

    // Initial tracking
    trackPresence();

    return () => {
      channel.untrack().then(() => channel.unsubscribe());
    };
  }, [postId, isAudioEnabled]);

  const startCall = async () => {
    try {
      setIsConnecting(true);
      setError(null);
      
      // Create Calls app instance first
      appRef.current = new CallsApp('5ac7099c0a99d23d46373dec9d19c729');
      
      // Get TURN credentials
      const turnCredentials = await appRef.current!.getTurnCredentials();
      
      // Get user media (audio only)
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      streamRef.current = stream;

      // Create peer connection
      pcRef.current = new RTCPeerConnection({
        iceServers: [turnCredentials.iceServers],
        bundlePolicy: 'max-bundle',
        iceTransportPolicy: 'all'
      });

      // Add connection state change listener
      pcRef.current.addEventListener('connectionstatechange', () => {
        const pc = pcRef.current;
        if (!pc || !appRef.current) return;

        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          stopCall();
        }
      });

      // Add audio tracks
      const transceivers = stream.getTracks().map(track =>
        pcRef.current!.addTransceiver(track, {
          direction: 'sendrecv'
        })
      );

      // Create session
      await pcRef.current.setLocalDescription(await pcRef.current.createOffer());
      const newSessionResult = await appRef.current.newSession(
        pcRef.current.localDescription!.sdp
      );
      await pcRef.current.setRemoteDescription(
        new RTCSessionDescription(newSessionResult.sessionDescription)
      );

      // Wait for connection
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Connection timeout')), 10000);
        pcRef.current!.addEventListener('iceconnectionstatechange', ev => {
          if ((ev.target as RTCPeerConnection).iceConnectionState === 'connected') {
            clearTimeout(timeout);
            resolve(true);
          }
        });
      });

      // Share tracks
      let trackObjects = transceivers.map(transceiver => ({
        location: 'local',
        mid: transceiver.mid,
        trackName: transceiver.sender.track!.id
      }));

      await pcRef.current.setLocalDescription(await pcRef.current.createOffer());
      const newLocalTracksResult = await appRef.current.newTracks(
        trackObjects,
        pcRef.current.localDescription!.sdp
      );
      await pcRef.current.setRemoteDescription(
        new RTCSessionDescription(newLocalTracksResult.sessionDescription)
      );
      
      // Handle incoming audio tracks
      pcRef.current.ontrack = (event) => {
        const [audioTrack] = event.streams[0].getAudioTracks();
        if (audioTrack) {
          const audioElement = new Audio();
          audioElement.srcObject = event.streams[0];
          audioElement.play().catch(console.error);
        }
      };

      setIsAudioEnabled(true);
    } catch (err) {
      console.error('Failed to start call:', err);
      setError(err instanceof Error ? err.message : 'Failed to start call');
      stopCall();
    } finally {
      setIsConnecting(false);
    }
  };
  const stopCall = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }

    // Clear participants when stopping
    participantsRef.current = new Set();
    setParticipants(new Set());
    setIsAudioEnabled(false);
  };


  React.useEffect(() => {
    return () => {
      stopCall();
    };
  }, []);

  return (
    <div className="bg-black/90 backdrop-blur-sm border-b border-gray-800">
      <div className="p-4">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-white font-semibold">Audio Discussion</h3>
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              participants.size > 0 ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-800 text-gray-400'
            }`}>
              <Waveform className={`w-4 h-4 ${participants.size > 0 ? 'animate-pulse' : ''}`} />
              <span>
                {participants.size > 0 ? `${participants.size} ${participants.size === 1 ? 'participant' : 'participants'}` : 'No participants'}
              </span>
            </div>
          </div>
          {!streamRef.current ? (
            <button
              onClick={startCall}
              disabled={isConnecting}
              className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isConnecting ? 'Connecting...' : 'Join Audio Chat'}
            </button>
          ) : (
            <button
              onClick={stopCall}
              title="Leave audio chat"
              className="bg-red-600 text-white px-4 py-2 rounded-full text-sm hover:bg-red-500 transition-colors"
            >
              Leave
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-500/10 text-red-400 p-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {isAudioEnabled && (
          <div className="bg-gray-800/50 rounded-lg p-4 flex flex-col items-center justify-center gap-4">
            <div className="flex items-center gap-4">
              <Waveform className="w-6 h-6 text-blue-400 animate-pulse" />
              <span className="text-white text-sm">You are connected to the audio chat</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}